import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { env } from '@/common/config';
import { playNotificationSound } from '@/common/utils/notificationAudio';

function streamUrl(companyId, token) {
  const base = String(env.apiBaseUrl || '').replace(/\/$/, '');
  const params = new URLSearchParams({ access_token: token });
  return `${base}/v1/notifications/companies/${companyId}/stream/?${params.toString()}`;
}

/**
 * Live notification_created events. EventSource reconnects itself.
 * shortcut: token is captured at connect time, remount after login refresh.
 */
export function useNotificationStream(companyId, onOpen) {
  const queryClient = useQueryClient();
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!companyId) return undefined;
    const token = localStorage.getItem('access_token');
    if (!token) return undefined;

    const source = new EventSource(streamUrl(companyId, token));

    const onCreated = (event) => {
      let notification;
      try {
        notification = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!notification || typeof notification !== 'object') return;

      queryClient.invalidateQueries({ queryKey: ['notifications', companyId] });
      playNotificationSound();

      const title = notification.title || 'Notification';
      const message = notification.message || '';
      toast(
        (t) => (
          <button
            type="button"
            className="text-left"
            onClick={() => {
              toast.dismiss(t.id);
              onOpenRef.current?.(notification);
            }}
          >
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {message ? (
              <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                {message}
              </p>
            ) : null}
          </button>
        ),
        { duration: 6000, position: 'top-right' },
      );
    };

    source.addEventListener('notification_created', onCreated);
    return () => {
      source.removeEventListener('notification_created', onCreated);
      source.close();
    };
  }, [companyId, queryClient]);
}
